import { Maybe } from '@/types/globals';
import { Injectable } from '@angular/core';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ApplicationModes, ProductTypes, SoftwareOptions } from '@shared/models/enums/enums';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { VersionsService } from '@shared/services/versions.service';
import { LimitedFeatures } from '@shared/models/limited-features';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OrderQuery } from '@modules/order/state/order-query';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { TypeEnum } from '@modules/order/models/type.enum';
import { ProcedureMap } from '@shared/models/procedure-map';

@Injectable({
	providedIn: 'root'
})
export class ScanOptionsVisibilityService {
	constructor(
		private readonly versionsService: VersionsService,
		private readonly hostPlatformService: HostPlatformService,
		private readonly softwareOptionsService: SoftwareOptionsService,
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery
	) {}

	isNiriCaptureVisible$: Observable<boolean> = combineLatest([
		this.hostPlatformService.isScanner$,
		this.softwareOptionsService.hasScannerSoftwareOption$(SoftwareOptions.ShouldEnableEvx),
		this.softwareOptionsService.hasScannerSoftwareOption$(SoftwareOptions.NGNIRI),
		this.shellQuery.productType$
	]).pipe(
		map(([isHostPlatformScanner, hasShouldEnableEvxSwo, hasNgNiriSwo, productType]) => {
			return isHostPlatformScanner
				? (productType === ProductTypes.evx && hasShouldEnableEvxSwo) || (productType === ProductTypes.ngPro && hasNgNiriSwo)
				: hasShouldEnableEvxSwo || hasNgNiriSwo;
		})
	);

	isSleeveConfirmationVisible$: Observable<boolean> = combineLatest([
		this.hostPlatformService.isScanner$,
		this.shellQuery.productType$,
		this.shellQuery.isRxSent$,
		this.shellQuery.clientVersion$,
		this.shellQuery.packageVersion$,
		this.softwareOptionsService.hasScannerSoftwareOption$(SoftwareOptions.ShouldEnableEvx),
		this.shellQuery.isSleeveConfirmed$
	]).pipe(
		map(
			([isHostPlatformScanner, productType, isRxSent, clientVersion, packageVersion, hasShouldEnableEvxSwo, isSleeveConfirmed]: [
				boolean,
				string,
				boolean,
				string,
				string,
				boolean,
				boolean
			]) => {
				if (isHostPlatformScanner) {
					if (isRxSent && isSleeveConfirmed) {
						return true;
					}

					const isSwoEnabled =
						((productType as ProductTypes) === ProductTypes.evx && hasShouldEnableEvxSwo) ||
						(!!productType && (productType as ProductTypes) !== ProductTypes.evx);

					return (
						isSwoEnabled &&
						this.versionsService.isFeatureAvailableForScanner(LimitedFeatures.SleeveConfirmation, clientVersion, packageVersion)
					);
				}

				return false;
			}
		)
	);

	isPalatalFeedbackVisible$: Observable<boolean> = combineLatest([
		this.orderQuery.procedureMap$,
		this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.PalateQC)
	]).pipe(
		map(([procedureMap, hasCompanySwo]) => {
			return (
				hasCompanySwo &&
				this.isPalatalFeedbackFeatureAvailableForPlatform(LimitedFeatures.PalatalScannigFeedback) &&
				this.isPalatalFeedbackAvailableForProcedure(procedureMap)
			);
		})
	);

	private isPalatalFeedbackAvailableForProcedure(procedureMap: Maybe<ProcedureMap>): boolean {
		const procedure = procedureMap?.ProcedureId as ProcedureEnum;

		if (procedure === ProcedureEnum.StudyModel_iRecord) {
			return true;
		}
		if (procedure === ProcedureEnum.Invisalign) {
			const type = procedureMap?.TypeId as TypeEnum;

			return type === TypeEnum.Invisalign_Aligners || type === TypeEnum.Vivera_Retainer || type === TypeEnum.Invisalign_Retainer;
		}

		return false;
	}

	private isPalatalFeedbackFeatureAvailableForPlatform(limitedFeatures: LimitedFeatures): boolean {
		if (this.hostPlatformService.isScanner) {
			return this.versionsService.isFeatureAvailableForScanner(
				limitedFeatures,
				this.shellQuery.clientVersion,
				this.shellQuery.packageVersion
			);
		}
		if (this.hostPlatformService.isMidc(this.shellQuery.applicationMode as ApplicationModes)) {
			const versions = this.shellQuery.scannersVersions?.map(s => s.VersionNumber);

			return this.versionsService.isFeatureAvailableForWeb(limitedFeatures, versions);
		}

		return false;
	}
}
